import React, { useEffect } from 'react';
import {
    Trophy,
    ShieldCheck,
    Users,
    Wallet,
    Target,
    FileText,
    Mic,
    Video,
    Eye,
    LayoutDashboard,
    Flame,
    FolderOpen,
    Wrench,
    Bot,
    TrendingUp,
    Building2
} from 'lucide-react';

const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

const NumRow = ({ n, h, s }) => (
    <div className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
        <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{n}</div>
        <div>
            <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{h}</h3>
            <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{s}</p>
        </div>
    </div>
);

export default function ChandraStrategy() {

    useEffect(() => {
        document.title = "Chandra Balaji | Brand Research";
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">Chandra Balaji</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Research</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Built from our call · 11 Sep 2026
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        The research<br />behind your brand.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-6 font-light">
                        Your positioning, your content series and the system that runs without your calendar.<br className="hidden sm:block" /> Everything below comes from our call and our read of your market. Nothing is templated.
                    </p>
                </section>

                {/* THE OBJECTIVE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The objective, stated plainly</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Leads first.<br />Views second.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You were clear on the call: you are not chasing followership, you want conversations that turn into deals. So this whole plan is built backwards from that, the way we run our own pages.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="Why your reach is stuck at 1K to 10K" s="Your current series decodes brands you find interesting. It attracts marketers who enjoy the breakdown, not the decision-makers who buy from CittaAI. The research problem is not effort, it is aim: what the market consumes, not what we want to make." />
                        <NumRow n="02" h="Niche content is the lead engine" s="The counterintuitive move: lower views, better inboxes. When the content goes deep on one industry's AI problems, only the people living those problems stay, comment and DM. That filter is the whole point. It is how a page with modest views books a full calendar." />
                        <NumRow n="03" h="Trust arrives before the meeting" s="Your own words: they should know you before the meeting. Every video is built so a prospect who checks your profile the night before a call finds an operator who has already answered their questions." />
                    </div>
                </section>

                {/* POSITIONING */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The positioning</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The operator who ships AI<br />inside real businesses.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            The AI conversation is owned by demo-sellers and hype accounts. Almost nobody speaks as the person who runs operations, builds the tooling and deploys it inside enterprises. That seat is empty in your market. You take it.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Building2} heading="Co-founder, COO and CMO" subheading="you run the operations and the growth at CittaAI · you speak from the driver's seat, not the sidelines" accent="indigo" />
                        <InfoCard icon={Wrench} heading="You build the tools" subheading="Uniflow: HRMS, CRM and operations in one agentic platform · proof you ship working systems, not slideware" accent="indigo" />
                        <InfoCard icon={TrendingUp} heading="Marketing is your native language" subheading="GTM builds, performance ads, event lead-gen · you have run the funnels your audience is trying to build" accent="indigo" />
                    </div>
                </section>

                {/* AUDIENCE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The audience read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Who this brand is for.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Users} heading="Your four sectors" subheading="decision-makers in real estate, pharma, premium marketing and government or public service · the businesses CittaAI already serves" accent="rose" />
                        <InfoCard icon={Flame} heading="Their fear, your fuel" subheading="they watch competitors adopt AI and worry about falling behind, but cannot tell real builders from demo-sellers · every video answers that fear with something shipped" accent="rose" />
                        <InfoCard icon={Target} heading="One viewer per script" subheading="every video is written for one specific decision-maker in one sector · narrow problem, broad hook, complete answer" accent="rose" />
                    </div>
                </section>

                {/* CONTENT SERIES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The content series</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Four series.<br />Aimed at buyers.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Your Decoding instinct stays, the aim changes: from decoding brands marketers admire to decoding what AI actually does inside the industries you sell to.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={FolderOpen} heading="AI Inside [Industry]" subheading="what AI can actually automate in a pharma plant, a real estate business, a government office · one sector, one problem, one complete answer per video" accent="amber" />
                        <InfoCard icon={Flame} heading="Decoding, re-aimed" subheading="your existing series DNA pointed at enterprise AI moves · why a deployment worked, what it cost, what it replaced · the breakdown only an operator can give" accent="amber" />
                        <InfoCard icon={Wrench} heading="The Operator's Log" subheading="building Uniflow and running CittaAI in public · SOPs, systems, what broke and what shipped · credibility content that compounds" accent="amber" />
                        <InfoCard icon={TrendingUp} heading="Against the Hype" subheading="the contrarian takes we discussed: when the market screams AGI, the operator says what the tech actually does today · disagreement is how you get remembered" accent="amber" />
                    </div>
                </section>

                {/* THE FUNNEL */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The funnel</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            From a comment<br />to a conversation.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            This is the part built for your leads-first objective. Followers are the input. Conversations are the output.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        <NumRow n="01" h="The resource play" s="Videos end with one word to comment. The resource fully solves a first problem for your sector audience, an AI readiness playbook only your ICP would even want. Whoever asks for it has qualified themselves." />
                        <NumRow n="02" h="The DM automation delivers" s="Our Meta-verified system sends the resource, nurtures the conversation and flags the serious ones. You wake up to a digest of people worth talking to." />
                        <NumRow n="03" h="The profile closes the loop" s="Bio, pinned content and story flow all point one way: a call with CittaAI. We bring the horse to the water; the meeting stays yours." />
                    </div>
                </section>

                {/* THE SYSTEM */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">The system</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Your brand runs<br />without your calendar.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            You said it on the call: recording time is the bottleneck. So we remove it. You stay the brain; the system does the rest.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Mic} heading="You drop voice notes" subheading="a take hits after a client call, you open WhatsApp · we ask the right questions to pull the insight out of you" accent="emerald" />
                        <InfoCard icon={FileText} heading="We research and script" subheading="aimed at what your market consumes, not what feels interesting · hooks, structure, words, in your voice" accent="emerald" />
                        <InfoCard icon={Bot} heading="Your clone records" subheading="trained on our fine-tuned pipeline with your voice and face · no filming days, no scheduling, no bottleneck" accent="emerald" />
                        <InfoCard icon={Eye} heading="You approve, we ship" subheading="nothing posts without your sign-off · daily cadence, one unified visual identity" accent="emerald" />
                        <InfoCard icon={LayoutDashboard} heading="The setter works your DMs" subheading="comments become qualified conversations become booked calls · with a dashboard you can check any hour" accent="emerald" />
                        <InfoCard icon={ShieldCheck} heading="The data runs the loop" subheading="every video tracked · what pulls your ICP gets volume, what pulls the wrong crowd gets cut" accent="emerald" />
                    </div>
                </section>

                {/* 90 DAYS + GUARANTEE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The 90 days</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            The roadmap<br />and the guarantee.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4 mb-10">
                        <NumRow n="W1" h="Onboarding" s="Your experience and takes downloaded, positioning locked, clone trained, first scripts approved. The heaviest back and forth happens here, on purpose." />
                        <NumRow n="W2" h="The engine goes live" s="Daily posting starts. Sector series and hooks tested against real data, not opinion." />
                        <NumRow n="W3-8" h="Test, then double down" s="What pulls your ICP gets volume. The resource and DM engine go live once the audience is real." />
                        <NumRow n="W9-12" h="Compound" s="Winning formats scale. The profile becomes the asset that walks into meetings before you do." />
                    </div>

                    <div className="max-w-3xl mx-auto">
                        <div className="rounded-2xl border border-amber-500/20 bg-amber-900/10 p-6 sm:p-8 text-center">
                            <div className="text-amber-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-4">The guarantee</div>
                            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed">
                                10,000 followers in 90 days, written into a legally bound contract. If we miss it, you get 100% of your money back. And because the content is built niche, those followers are the people you sell to, not random viewers.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        We lock the number on our call.
                    </h2>
                    <p className="text-base sm:text-lg text-white/55 max-w-xl mx-auto font-light leading-relaxed mb-10">
                        The research is done and the system is ready. Week 1 starts the day we sign.
                    </p>
                    <a
                        href="mailto:suresh@socialseo.in"
                        className="inline-block bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold px-8 py-4 rounded-full text-sm sm:text-base"
                    >
                        suresh@socialseo.in
                    </a>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
